import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import type { SnowplowClientEventParams } from 'analytics/snowplow/events'
import { ChertoExperiments } from 'constants/experiments'
import { ALL_HOTEL_TYPES, LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'
import { MAX_CAROUSEL_ITEM_COUNT } from './common/constants'

export type PersonalisedCarouselId = 'family-friendly' | 'weekend-stays' | 'post-purchase-hotels'

// Shared slot priority confirmed for launch: recent searches, then family.
// Weekend stays remains last until CRO-7353 confirms its place.
export const PERSONALISED_CAROUSEL_PRIORITY: ReadonlyArray<PersonalisedCarouselId> = [
  'post-purchase-hotels', 'family-friendly', 'weekend-stays',
]
export const PERSONALISED_CAROUSEL_PRIORITY_CONFIRMED = true

const CAROUSEL_EXPERIMENT_IDS: Partial<Record<PersonalisedCarouselId, string>> = {
  'family-friendly': ChertoExperiments.croHomepageFamilyFriendlyOfferCarousel.id,
  'post-purchase-hotels': ChertoExperiments.homePageRecentSearchesCarousel.id,
}

export interface PersonalisedCarouselCandidate {
  id: PersonalisedCarouselId
  status: 'loading' | 'eligible' | 'ineligible'
}

export interface PersonalisedCarouselSelection {
  selected?: PersonalisedCarouselId
  eligible: Array<PersonalisedCarouselId>
  suppressed: Array<PersonalisedCarouselId>
  reason: 'eligibility_pending' | 'priority_pending' | 'priority_selected' | 'single_eligible' | 'none_eligible'
}

export function selectPersonalisedCarousel(
  candidates: ReadonlyArray<PersonalisedCarouselCandidate>,
  priorityConfirmed = PERSONALISED_CAROUSEL_PRIORITY_CONFIRMED,
): PersonalisedCarouselSelection {
  const eligible = PERSONALISED_CAROUSEL_PRIORITY.filter(id => candidates.some(candidate => candidate.id === id && candidate.status === 'eligible'))

  const selected = eligible[0]
  const loadingCouldChangeSelection = candidates.some(candidate => {
    if (candidate.status !== 'loading') return false
    if (!priorityConfirmed || !selected) return true

    // Once priority is confirmed, unresolved lower-priority candidates cannot
    // displace the current winner and must not delay either of its arms.
    return PERSONALISED_CAROUSEL_PRIORITY.indexOf(candidate.id) < PERSONALISED_CAROUSEL_PRIORITY.indexOf(selected)
  })

  if (loadingCouldChangeSelection) {
    return { eligible, suppressed: eligible, reason: 'eligibility_pending' }
  }

  if (eligible.length > 1 && !priorityConfirmed) {
    return { eligible, suppressed: eligible, reason: 'priority_pending' }
  }

  if (!selected) {
    return { eligible, suppressed: [], reason: 'none_eligible' }
  }

  return {
    selected,
    eligible,
    suppressed: eligible.filter(id => id !== selected),
    reason: eligible.length > 1 ? 'priority_selected' : 'single_eligible',
  }
}

export function buildFamilyFriendlyOfferFilters(manualSortingEnabled: boolean): App.OfferListFilters {
  const filters: App.OfferListFilters = {
    holidayTypes: ['Family Friendly'],
    limit: MAX_CAROUSEL_ITEM_COUNT,
  }

  // Match HotelOfferCarousel's homepage inventory and ordering in both modes.
  if (manualSortingEnabled) {
    return { ...filters, offerTypes: ALL_HOTEL_TYPES, sortBy: 'carousel-hotel' }
  }

  return { ...filters, offerTypes: LE_CONTRACTED_HOTEL_TYPES, frontPageVisibleOnly: true }
}

export function buildSearchSlotSelectionEvent(decision: PersonalisedCarouselSelection): SnowplowClientEventParams {
  const selectedExperiment = decision.selected ? CAROUSEL_EXPERIMENT_IDS[decision.selected] : undefined

  return {
    subject: decision.selected ?? 'none',
    action: decision.reason,
    category: 'homepage-personalised-carousel-selection',
    type: 'operational',
    context: [
      comluxgroup.createFilter_1_0_0({ type: 'SelectedExperiment', label: 'Selected experiment', value: selectedExperiment ?? 'none' }),
      comluxgroup.createFilter_1_0_0({ type: 'EligibleCarousels', label: 'Eligible carousels', value: decision.eligible.join(',') || 'none' }),
      comluxgroup.createFilter_1_0_0({ type: 'SuppressedCarousels', label: 'Suppressed carousels', value: decision.suppressed.join(',') || 'none' }),
    ],
  }
}
